import { ChangeEvent, useCallback, useEffect, useState } from "react"

import { ApiError, ErrorFields, ErrorsMap } from "api/http/utils"
import { ApprovalLevel, ClientApproval } from "models/clientAgencyConf/profile"
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
} from "@material-ui/core"
import { css, cx } from "@emotion/css"
import {
  fetchAgencyClientApproval,
  fetchClientConfigApproval,
  updateClientAgencyApproval,
  updateClientConfigApproval,
} from "api/clients"
import { queryCache, useMutation, useQuery } from "react-query"

import Button from "ui/buttons/Button"
import { Levels } from "./Levels"
import { Loading } from "components/Loading"
import { agencyClientStyles } from "./styles"
import { createDataSource } from "components/Bable"
import { set } from "lodash/fp"
import { useAuthenticated } from "providers/A12n"
import { useFeedback } from "components/FeedbackSnackbar"
import { useHistory } from "react-router-dom"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router-dom"
import { useAgencyId } from "hooks/useAgencyId"

const getLevelRowId = (x: ApprovalLevel) => x.id
export const levelsState = createDataSource({ getRowId: getLevelRowId })

export function AgencyClientFlow() {
  const { t } = useI18n()
  let history = useHistory()
  const { id, configId } = useParams<{ id: string; configId: string }>()
  const sendFeedback = useFeedback((s) => s.send)
  const [approval, setApproval] = useState<Partial<ClientApproval>>()
  const [errors, setErrors] = useState<ErrorsMap>()
  const [levelErrors, setLevelErrors] = useState<
    Record<string, Record<string, string>> | undefined
  >(undefined)
  const { hasAuthority, meta } = useAuthenticated()
  const isTeamLead = meta?.teamLead
  const { agencyId } = useAgencyId()

  const approvalOptions = ["INTERNAL", "CLIENT"].map((ao) => ({
    label: t(`agency.client.approval.option.${ao}`.toLowerCase()),
    value: ao,
  }))

  const clientOptions = ["THRESHOLD_LEVELS", "MANUAL"].map((ao) => ({
    label: t(`agency.client.approved.option.${ao}`.toLowerCase()),
    value: ao,
  }))

  const queryFn = isTeamLead
    ? fetchClientConfigApproval
    : fetchAgencyClientApproval
  const queryId = isTeamLead ? configId : id
  const enabled = isTeamLead ? queryId : agencyId

  const { data, isLoading, updatedAt } = useQuery(
    ["agency-client-approval", queryId, agencyId],
    queryFn,
    {
      enabled,
      onError: (e) => sendFeedback(t(e.message), "error"),
    },
  )

  const handleChange = (
    evt: ChangeEvent<
      | HTMLInputElement
      | {
          name?: string | undefined
          value: unknown
        }
    >,
    extra?: boolean | string,
  ) => {
    const { name, value } = evt?.target
    const v = extra !== undefined ? extra : value
    !!name &&
      setApproval((s) => {
        const changed = set(name, v, s || {})
        return changed
      })
  }

  const handleTeamsClick = () => {
    history.push(`/agency-clients/${id}/teams/${configId}`)
  }

  const patchFn = isTeamLead
    ? updateClientConfigApproval
    : updateClientAgencyApproval
  const [updateApproval, { isLoading: isSavePending }] = useMutation(patchFn, {
    onSuccess: () => {
      queryCache.invalidateQueries("agency-client-approval")
      sendFeedback(t("agency.client.flow.success"), "success")
      setErrors(undefined)
    },
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)

      const fields = error?.fields as ErrorFields & {
        approvalFlowLevel?: Record<string, Record<string, string>>
      }
      !!fields && setLevelErrors(fields?.approvalFlowLevel)
    },
  })

  const updateLevelRow = useCallback(
    (level: ApprovalLevel, remove?: boolean) => {
      const { ...values } = level
      setApproval((app) => {
        const updatedLevels = [
          ...(app?.approverLevels?.filter((al) => al.id !== values.id) || []),
          ...(remove ? [] : [values]),
        ]

        const updated = { ...app, approverLevels: updatedLevels }

        return updated
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, agencyId],
  )

  const onSave = () => {
    if (!isTeamLead) {
      agencyId && updateApproval({ agencyId, clientId: id, ...approval })
    } else {
      updateApproval({ clientId: configId, ...approval })
    }
  }

  useEffect(() => {
    !!data && setApproval(data)
    levelsState.setData(data?.approverLevels || [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedAt])

  const noTeamLead = !data?.planningTeamLeads?.length
  const noBuyingTeamLead = !data?.buyingTeamLeads?.length
  const allowed = hasAuthority(["AUTH_ASSIGN_AGENCY_CLIENT_TEAM"])

  return isLoading ? (
    <Loading />
  ) : (
    <Box className={styles.wrapper}>
      <Box className={styles.clientApprovalHeader}>
        <h5 className={cx("BodyBody-1BlackBold", styles.flowTitle)}>
          {t("agency.client.approval")}:
        </h5>
        {hasAuthority(["AUTH_EDIT_FLOW"]) && (
          <Button
            disabled={isSavePending}
            color="primary"
            variant="contained"
            onClick={onSave}
          >
            {t("save")}
          </Button>
        )}
      </Box>
      <Box className={styles.clientApproval}>
        <FormControlLabel
          value="start"
          label={t("agency.client.requires.approval")}
          labelPlacement="start"
          classes={{
            labelPlacementStart: styles.labelPlacementStart,
            label: styles.label,
          }}
          control={
            <Switch
              name="requiresApproval"
              checked={!!approval?.requiresApproval}
              onChange={handleChange}
              disabled={!hasAuthority(["AUTH_EDIT_FLOW"])}
              color={hasAuthority(["AUTH_EDIT_FLOW"]) ? "primary" : undefined}
            />
          }
        />

        {!!approval?.requiresApproval ? (
          <RadioGroup
            className={agencyClientStyles.radioGroup}
            aria-label="vendor type"
            name="approvalProcess"
            value={approval?.approvalProcess}
            onChange={handleChange}
          >
            {approvalOptions.map((vt) => (
              <FormControlLabel
                key={vt.value}
                value={vt.value}
                label={t(vt.label)}
                control={<Radio color="primary" />}
                disabled={!hasAuthority(["AUTH_EDIT_FLOW"])}
              />
            ))}
          </RadioGroup>
        ) : null}
      </Box>
      {!!approval?.requiresApproval ? (
        <Box
          className={cx(styles.approvalContent, {
            [styles.extraPadding]: approval?.approvalProcess === "CLIENT",
          })}
        >
          {approval?.approvalProcess === "CLIENT" ? (
            <Box className={styles.clientApprovers}>
              <h5 className="BodyBody-1BlackBold">
                {t("agency.client.approved.title")}
              </h5>
              <RadioGroup
                className={agencyClientStyles.radioGroup}
                aria-label="vendor type"
                name="clientApprovalProcess"
                value={approval?.clientApprovalProcess}
                onChange={handleChange}
                defaultValue={"THRESHOLD_LEVELS"}
              >
                {clientOptions.map((vt) => (
                  <FormControlLabel
                    key={vt.value}
                    value={vt.value}
                    label={t(vt.label)}
                    control={<Radio color="primary" />}
                    disabled={!hasAuthority(["AUTH_EDIT_FLOW"])}
                    className="BodyBody-2BlackLight"
                  />
                ))}
              </RadioGroup>
              <>
                {approval?.clientApprovalProcess === "THRESHOLD_LEVELS" ? (
                  <p className={"BodyBody-2BlackLight"}>
                    {t("client.thresholdLevels.selected.text")}
                  </p>
                ) : (
                  <p className="BodyBody-2BlackLight">
                    {t("client.manual.selected.text")}
                  </p>
                )}
                <Levels
                  updateLevelRow={updateLevelRow}
                  errors={levelErrors}
                  approvalProcess={approval?.clientApprovalProcess}
                />
              </>
              {!!errors?.approverLevels ? (
                <span className="BodyBody-xsRedRegular">
                  {t(errors.approverLevels)}
                </span>
              ) : null}
            </Box>
          ) : (
            <div className={styles.teamLeads}>
              <span className="BodyBody-2BlackBold">
                {t("planning.team.leads")}:
              </span>
              <span className="BodyBody-1BlackRegular">
                <span>{data?.planningTeamLeads.join(", ")} </span>
              </span>
              {noTeamLead ? (
                <span className="HeadlineH6Dark-greyLight">
                  {!allowed
                    ? t("team.lead.empty")
                    : t("team.lead.empty.manage")}
                </span>
              ) : null}
              {allowed ? (
                <div
                  className={cx("BodyBody-sBlackLight", styles.teamLeadsText)}
                >
                  <span>{t("agency.client.team.lead.teams.first_text")}</span>
                  <span
                    onClick={handleTeamsClick}
                    className={styles.teamLeadsButton}
                  >
                    "{t("agency.client.team.lead.teams.button")}"
                  </span>
                  <span>{t("agency.client.team.lead.teams.second_text")}</span>
                </div>
              ) : null}
            </div>
          )}
        </Box>
      ) : null}

      <FormControlLabel
        value="start"
        label={t("agency.client.authorized")}
        labelPlacement="start"
        classes={{
          labelPlacementStart: styles.labelPlacementStart,
          label: styles.label,
        }}
        control={
          <Switch
            disabled={true}
            name="requiresAuthorization"
            checked={false}
            color="primary"
          />
        }
      />
      <h5 className={cx("BodyBody-1BlackBold", styles.title)}>
        {t("agency.client.campaign.bank.can.be")}:
      </h5>
      <FormControlLabel
        value="start"
        label={t("agency.client.bank.enabled")}
        labelPlacement="start"
        classes={{
          labelPlacementStart: styles.labelPlacementStart,
          label: styles.label,
        }}
        control={
          <Switch
            disabled={true}
            name="campaignBankUsage"
            checked={false}
            color="primary"
          />
        }
      />
      <h5 className={cx("BodyBody-1BlackBold", styles.title)}>
        {t("agency.client.booking.orders.can.be")}:
      </h5>
      <FormControlLabel
        value="start"
        label={t("agency.client.booking.orders.approved")}
        labelPlacement="start"
        classes={{
          labelPlacementStart: styles.labelPlacementStart,
          label: styles.label,
        }}
        control={
          <Switch
            name="bookingOrderRequiresApproval"
            checked={!!approval?.bookingOrderRequiresApproval}
            onChange={handleChange}
            disabled={!hasAuthority(["AUTH_EDIT_FLOW"])}
            color={hasAuthority(["AUTH_EDIT_FLOW"]) ? "primary" : undefined}
          />
        }
      />
      {!!approval?.bookingOrderRequiresApproval ? (
        <Box className={styles.approvalContent}>
          <div className={styles.teamLeads}>
            <span className="BodyBody-2BlackBold">
              {t("buying.team.leads")}:
            </span>
            <span className="BodyBody-1BlackRegular">
              <span>{data?.buyingTeamLeads.join(", ")} </span>
            </span>
            {noBuyingTeamLead ? (
              <span className="HeadlineH6Dark-greyLight">
                {!allowed ? t("team.lead.empty") : t("team.lead.empty.manage")}
              </span>
            ) : null}
            {allowed ? (
              <div className={cx("BodyBody-sBlackLight", styles.teamLeadsText)}>
                <span>{t("agency.client.team.lead.teams.first_text")}</span>
                <span
                  onClick={handleTeamsClick}
                  className={styles.teamLeadsButton}
                >
                  "{t("agency.client.team.lead.teams.button")}"
                </span>
                <span>{t("agency.client.team.lead.teams.second_text")}</span>
              </div>
            ) : null}
          </div>
        </Box>
      ) : null}
      <h5 className={cx("BodyBody-1BlackBold", styles.title)}>
        {t("agency.client.costs.transparency")}:
      </h5>
      <div className={styles.costsWrapper}>
        <FormControlLabel
          value="start"
          label={t("agency.client.costs.transparency.enabled")}
          labelPlacement="start"
          classes={{
            labelPlacementStart: styles.labelPlacementStart,
            label: styles.label,
          }}
          control={
            <Switch
              name="costsTransparency"
              checked={!!approval?.costsTransparency}
              onChange={handleChange}
              disabled={!hasAuthority(["AUTH_EDIT_FLOW"])}
              color={hasAuthority(["AUTH_EDIT_FLOW"]) ? "primary" : undefined}
            />
          }
        />
        {!!approval?.costsTransparency ? (
          <span className={cx("BodyBody-2BlackLight", styles.costsText)}>
            {t("agency.client.costs.transparency.text")}
          </span>
        ) : null}
      </div>
    </Box>
  )
}

const styles = {
  wrapper: css`
    display: grid;
    align-items: center;
    justify-content: start;
    justify-items: start;
    grid-auto-flow: row;
    grid-auto-columns: 100%;
    grid-row-gap: var(--spacing-xs);
    padding: var(--spacing-xxl-2) 0;
    max-width: 920px;
  `,
  clientApprovalHeader: css`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: end;
  `,
  clientApproval: css`
    display: grid;
    grid-auto-columns: max-content;
    grid-auto-flow: column;
    grid-column-gap: var(--spacing-m);
  `,
  approvalContent: css`
    padding: var(--spacing-l) 0;
    padding-bottom: var(--spacing-xxl-3);
    display: grid;
    grid-auto-flow: row;
    grid-auto-columns: minmax(350px, max-content);
  `,
  clientApprovers: css`
    padding-left: 17px;
  `,
  extraPadding: css`
    padding: var(--spacing-xxl-2) 0;
  `,
  title: css`
    margin-top: 20px;
  `,
  labelPlacementStart: css`
    margin: 0;
  `,
  label: css`
    min-width: 70px;
  `,
  teamLeads: css`
    align-items: center;
    padding-left: 140px;
    display: grid;
    grid-auto-columns: max-content;
    grid-row-gap: var(--spacing-xs);
    > span {
      margin: 0 var(--spacing-xxs) 0 0;
    }
  `,
  teamLeadsText: css`
    display: flex;
    align-items: center;
    padding-right: 3px;
    gap: 2px;
  `,
  teamLeadsButton: css`
    color: var(--primary-dark);
    font-weight: 400;
    cursor: pointer;
  `,
  costsWrapper: css`
    display: flex;
    align-items: center;
  `,
  costsText: css`
    margin-left: var(--spacing-xs);
    width: 400px;
  `,
  flowTitle: css`
    padding-bottom: var(--spacing-xs);
  `,
}
